import type { FaqItem } from "@/components/sections/products/shared/faq/types";
import { DOCS, ROUTES, STATS, TRUSTPILOT_URL } from "@/lib/constants";

export const RESIDENTIAL_PRODUCT_FAQS: FaqItem[] = [
  {
    q: "What are residential proxies?",
    a: "Residential proxies route your traffic through IP addresses assigned by residential ISPs. Because the IPs look like ordinary household internet connections, they're trusted by websites and far less likely to be blocked than datacenter IPs.",
  },
  {
    q: "How are Proxidize residential IPs sourced?",
    a: "Residential IPs are sourced ethically through our own bandwidth-sharing app alongside carefully selected partner applications. All traffic is obtained under strict consent-based standards.",
  },
  {
    q: "How many residential IPs does Proxidize have?",
    a: `${STATS.ips} residential IPs across ${STATS.countries} countries, with targeting available down to the city and ISP level.`,
  },
  {
    q: "What can I use residential proxies for?",
    a: "Common use cases include web scraping, price monitoring, SEO and SERP tracking, AI and LLM training data collection, market research, and brand protection. We only allow legal and ethical use cases, as described in our Acceptable Use Policy — use relating to the adult industry or .gov sites is not permitted.",
  },
  {
    q: "Can I use Proxidize residential proxies with AI agents?",
    a: "Yes. Rotating residential IPs blend agent traffic in with normal user activity, so automated browsing and task execution are less likely to get blocked. Connect the agent's actual traffic via HTTP, HTTPS, or SOCKS5 — Playwright and Puppeteer examples are in our developer docs. The REST API and the open-source Proxidize MCP server are separate interfaces for managing your proxy resources (rotating IPs, switching locations, monitoring usage), including directly from Claude Code, Claude Desktop, or Cursor.",
    link: { label: "Proxidize MCP server", href: "/tools/mcp-server" },
  },
  {
    q: "How does geo-targeting work?",
    a: "Select your target country, city, or ISP from the dashboard. Every request routes through the location you specify.",
    link: { label: "Residential proxy docs", href: DOCS.residentialProxies },
  },
  {
    q: "Do sessions rotate or stay sticky?",
    a: "Both. You can rotate your IP on every request or hold a sticky session, depending on your workflow.",
    link: { label: "Residential API reference", href: DOCS.apiPerGbResidential },
  },
  {
    q: "What happens to unused bandwidth?",
    a: "It rolls over to the next month. If you cancel your plan, your remaining bandwidth stays in your account and never expires. You can use it whenever you want.",
  },
  {
    q: "Is KYC required?",
    a: "Yes. Every customer selects a plan first, then completes Tier 1 KYC (Know Your Customer) verification from their account. Proxy access stays locked until verification is approved.",
    link: { label: "KYC requirements", href: ROUTES.kyc },
  },
  {
    q: "What protocols are supported?",
    a: "HTTP, HTTPS, and SOCKS5.",
  },
  {
    q: "Is there a money-back guarantee?",
    a: "Yes. Refund requests submitted in writing within 30 days are honored provided no more than 10 GB has been used on the individual proxy, up to a maximum of two refunds per customer account.",
  },
  {
    q: "Are Proxidize residential proxies dedicated or static?",
    a: "No. The residential product provides access to a rotating residential proxy pool with optional sticky sessions. A sticky session can hold an exit temporarily, but it isn't the same as a permanently dedicated static IP.",
  },
  {
    q: "Is Proxidize a legitimate residential proxy provider?",
    a: "Yes. Proxidize is rated 4.8/5 across verified reviews on G2 and Trustpilot, requires KYC verification from every customer, and backs every plan with a 30-day money-back guarantee.",
    link: { label: "Read verified reviews", href: TRUSTPILOT_URL },
  },
  {
    q: "Are there contracts, monthly minimums, or hidden fees?",
    a: "No long-term contracts and no hidden fees. Plans start at $25/month for 25 GB ($1/GB) and renew automatically each month until you cancel — cancel anytime with no penalty, and any unused bandwidth simply rolls over. Scale your plan up or down whenever you like.",
  },
  {
    q: "Can I target UK residential IPs specifically?",
    a: `Yes. The UK is one of ${STATS.countries} supported countries, and you can narrow targeting down to specific UK cities and ISPs from the dashboard.`,
  },
];
